'use client';

import React, { useMemo } from 'react';
import { useParams } from 'next/navigation';
import { useProfile } from 'src/context/providers/ProfileProvider';
import useGetPublications from 'src/hooks/publications/useGetPublications';
import {
  CircularProgress,
  Box,
  Card,
  ProfileCardHeader,
  Typography,
} from 'src/components';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faBook } from '@fortawesome/pro-solid-svg-icons';
import { PublicationItem } from 'src/components/Publications/components';
import Link from 'next/link';
import { getProfileUrl } from 'src/utils/profileUrl';

const ProfilePublications = () => {
  const { entityDetails } = useProfile();
  const { type, id } = useParams<{ type: string; id: string }>();

  const { data: publications, isLoading: isLoadingPublications } =
    useGetPublications({ providerId: entityDetails?.provider_id });

  const { visiblePubs, additionalPubs } = useMemo(() => {
    if (!publications) return { visiblePubs: [], additionalPubs: [] };
    return {
      visiblePubs: publications?.results?.slice(0, 3),
      additionalPubs: publications?.results?.slice(3),
    };
  }, [publications]);

  const renderContent = () => {
    if (isLoadingPublications) {
      return <CircularProgress />;
    }

    return (
      <div className="flex flex-col">
        {visiblePubs?.map((publication) => (
          <PublicationItem key={publication.id} publication={publication} />
        ))}
        {additionalPubs?.length > 0 && (
          <Link
            href={getProfileUrl(type, id, 'publications')}
            className="flex flex-row gap-2 items-center"
          >
            <Typography
              variant="caption"
              bold
              className="py-2 text-link cursor-pointer hover:text-sky-500 transition-colors duration-200 flex items-center gap-2"
            >
              View all
              <Icon icon={faArrowRight} />
            </Typography>
          </Link>
        )}
      </div>
    );
  };

  if (publications?.results?.length === 0) return null;

  return (
    <Card className="p-6 bg-white w-full h-full">
      <Box className="flex flex-col">
        <ProfileCardHeader icon={faBook} title="Publications" />
        <Box>{renderContent()}</Box>
      </Box>
    </Card>
  );
};

export default ProfilePublications;
